import React from 'react';
import { Row, Col, Card, Typography, Skeleton, Table, Tag, Space, Button } from 'antd';
import { InboxOutlined, ShoppingCartOutlined, ToolOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import dayjs from 'dayjs';
import useAuthStore from '../store/useAuthStore';
import StatsCard from '../components/common/StatsCard';
import { getStockChartData, getRecentPurchases, getLowStockItems } from '../api/reports.api';

const { Title, Text } = Typography;

const COLORS = ['#1a56db', '#0e9f6e', '#ff8800', '#8b5cf6', '#ec4899'];

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const plantId = user?.plantId;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Fetch data
  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey: ['dashboard-stock', plantId],
    queryFn: () => getStockChartData(plantId),
    enabled: !!plantId
  });

  const { data: purchasesData, isLoading: purchasesLoading } = useQuery({
    queryKey: ['dashboard-purchases', plantId],
    queryFn: () => getRecentPurchases(plantId),
    enabled: !!plantId
  });

  const { data: lowStockData, isLoading: lowStockLoading } = useQuery({
    queryKey: ['dashboard-lowstock', plantId],
    queryFn: () => getLowStockItems(plantId),
    enabled: !!plantId
  });

  // Calculate KPIs from stock data
  const totalRawMaterials = stockData?.length || 0;
  const criticalStockCount = stockData?.filter(item => item.balanceQty <= 0).length || 0;
  const purchasesThisMonth = purchasesData?.items?.reduce((sum, p) => sum + (p.totalAmount || 0), 0) || 0;
  const todayProductions = 0; // Mock data

  // Prepare chart data
  const stockChartData = stockData?.slice(0, 10).map(item => ({
    name: item.rawName?.substring(0, 12) || '',
    balance: item.balanceQty || 0,
    status: item.balanceQty <= 0 ? 'critical' : item.balanceQty < 10 ? 'low' : 'good',
    fullName: item.rawName,
    unit: item.unitDisplay
  })) || [];

  const productionChartData = [
    { name: 'Product A', value: 400 },
    { name: 'Product B', value: 300 },
    { name: 'Product C', value: 200 },
  ];

  const purchaseColumns = [
    { title: 'Bill No', dataIndex: 'billNo', key: 'billNo' },
    { title: 'Date', dataIndex: 'billDate', key: 'billDate', render: (date) => dayjs(date).format('DD MMM') },
    { title: 'Vendor', dataIndex: 'vendorName', key: 'vendorName' },
    { title: 'Amount', dataIndex: 'totalAmount', key: 'totalAmount', render: (amt) => <Text style={{ color: '#0e9f6e', fontWeight: 600 }}>₹{amt?.toLocaleString()}</Text> },
  ];

  const lowStockColumns = [
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Balance', dataIndex: 'balanceQty', key: 'balanceQty', render: (qty) => <Text strong>{qty}</Text> },
    { title: 'Status', dataIndex: 'stockStatus', key: 'stockStatus', render: (status) => (
      <Tag color={status === 'Critical' ? 'red' : 'orange'}>{status}</Tag>
    )},
  ];

  return (
    <div className="page-enter">
      {/* Welcome Bar */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
          {getGreeting()}, {user?.userName || user?.loginId}! 👋
        </Title>
        <Text style={{ fontSize: 14, color: '#64748b' }}>
          Plant: {user?.plantName} • {dayjs().format('dddd, MMMM D, YYYY')}
        </Text>
      </div>

      {/* KPI Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          {stockLoading ? <Skeleton.Button active block style={{ height: 140 }} /> : (
            <StatsCard
              icon={<InboxOutlined />}
              title="Total Raw Materials"
              value={totalRawMaterials}
              color="#1a56db"
            />
          )}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {purchasesLoading ? <Skeleton.Button active block style={{ height: 140 }} /> : (
            <StatsCard
              icon={<ShoppingCartOutlined />}
              title="Purchases This Month"
              value={`₹${purchasesThisMonth.toLocaleString()}`}
              color="#0e9f6e"
            />
          )}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard
            icon={<ToolOutlined />}
            title="Productions Today"
            value={todayProductions}
            color="#ff8800"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {stockLoading ? <Skeleton.Button active block style={{ height: 140 }} /> : (
            <StatsCard
              icon={<WarningOutlined />}
              title="Critical Stock Items"
              value={criticalStockCount}
              color="#f05252"
              onClick={() => navigate('/dashboard/masters/raw-materials?lowStock=true')}
            />
          )}
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card title="Raw Material Stock Levels" style={{ height: 360 }}>
            {stockLoading ? <Skeleton active paragraph={{ rows: 6 }} /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stockChartData}>
                  <XAxis dataKey="name" style={{ fontSize: 12 }} />
                  <YAxis style={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload[0]) {
                        const data = payload[0].payload;
                        return (
                          <div style={{ background: '#fff', padding: 8, border: '1px solid #e2e8f0', borderRadius: 4 }}>
                            <Text strong>{data.fullName}</Text><br />
                            <Text>Balance: {data.balance} {data.unit}</Text>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="balance" fill="#1a56db" radius={[8, 8, 0, 0]}>
                    {stockChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.status === 'critical' ? '#f05252' : entry.status === 'low' ? '#ff8800' : '#0e9f6e'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Production This Month" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={productionChartData}
                  cx="50%"
                  cy="45%"
                  innerRadius={60}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  {productionChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Tables Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={13}>
          <Card
            title={
              <Space>
                <ShoppingCartOutlined />
                <span>Recent Purchases</span>
              </Space>
            }
            extra={<Button type="link" onClick={() => navigate('/dashboard/transactions/purchase')}>View All</Button>}
          >
            {purchasesLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
              <Table
                columns={purchaseColumns}
                dataSource={purchasesData?.items || []}
                pagination={false}
                size="small"
                rowKey="rpsid"
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={11}>
          <Card
            title={
              <Space>
                <WarningOutlined style={{ color: '#f05252' }} />
                <span>⚠️ Low Stock Alert</span>
              </Space>
            }
            extra={<Button type="link" onClick={() => navigate('/dashboard/reports/stock')}>View All</Button>}
          >
            {lowStockLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
              lowStockData && lowStockData.length > 0 ? (
                <Table
                  columns={lowStockColumns}
                  dataSource={lowStockData.slice(0, 5)}
                  pagination={false}
                  size="small"
                  rowKey="rawID"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#0e9f6e' }}>
                  <Text style={{ fontSize: 16 }}>✅ All stock levels are healthy</Text>
                </div>
              )
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
